import React, { useEffect, useState } from "react";
import { UseFormSetError } from "react-hook-form";
import {
  AddCollaboratorsHistoryTechSkillRequestList,
  CollaboratorDetails,
  CollaboratorsHistoryTechSkill,
} from "../../../../../../../types/api/collaborators.type";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  ADD_NEW_TECH_SKILL,
  ATTACH_TECH_SKILL,
  CANCEL_LABEL,
  CONFIRM_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  SAVE_LABEL,
  TECH_SKILL_DUPLICATE_ENTITY_ERROR_MESSAGE,
  TECH_SKILL_DUPLICATE_PROP_ERROR_MESSAGE,
} from "../../../../../../../constants/Dictionary";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import { ReactComponent as SaveIcon } from "../../../../../../../icons/save.svg";
import { ReactComponent as CancelIcon } from "../../../../../../../icons/close.svg";
import { ReactComponent as ConfirmIcon } from "../../../../../../../icons/check.svg";
import {
  StyledAlertMessage,
  StyledHistoryTechSkillModal,
} from "./CollaboratorTechSkillsModal.styled";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import {
  FontWeightEnum,
  IconSizeEnum,
  TechSkillModalMode,
} from "../../../../../../../types/AppTypes";
import { IconManager } from "../../../../../../../components/core/IconManager/IconManager";
import { theme } from "../../../../../../../constants/StyledComponents";
import { BodyTyp } from "../../../../../../../components/Typography/Typography";
import { ReactComponent as WarningIcon } from "../../../../../../../icons/warning.svg";
import { TechSkillForm } from "../components/TechSkillsForm";
import {
  AttachHistoryTechSkillTable,
  AttachSelectableTechSkill,
} from "../components/AttachHistoryTechSkillTable";
import { AddTechSkillRequest } from "../../../../../../../types/api/techSkills.type";
import { useAppSelector } from "../../../../../../../hooks/store.hook";
import { useAddTechSkill } from "../../../../../../../hooks/techSkills.hooks";
import { useHistoryTechSkillsAdd } from "../../../../../../../hooks/professionalHistory.hook";
import {
  CustomerErrorCodesEnum,
  StatusCodes,
} from "../../../../../../../constants/AppConstants";
import { useNotification } from "../../../../../../../hooks/notification.hook";
import { AxiosError } from "axios";
import { SingleErrorResponse } from "../../../../../../../types/api/generic.type";

export type FormDataTech = {
  competence: string;
  stream: string;
  software: string;
  component?: string;
  description?: string;
  level?: string | null;
};

type CollaboratorTechSkillsModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  record?: CollaboratorsHistoryTechSkill;
  collaborator: CollaboratorDetails;
};

export const CollaboratorTechSkillsModal = ({
  isOpen,
  onCancel,
  onConfirm,
  record,
  collaborator,
}: CollaboratorTechSkillsModalProps) => {
  const { notification } = useNotification();

  const [mode, setMode] = useState<TechSkillModalMode>(
    TechSkillModalMode.ATTACH,
  );
  const [selectedTechSkills, setSelectedTechSkills] = useState<
    AttachSelectableTechSkill[]
  >([]);
  const [isDuplicateError, setIsDuplicateError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMode(TechSkillModalMode.ATTACH);
      setSelectedTechSkills([]);
    }
  }, [isOpen]);

  const { orgSettings } = useAppSelector((state) => state.config);
  const profileId = collaborator.id;

  const { addTechSkill, isAdding: isAddingTechSkill } = useAddTechSkill();
  const {
    mutateAsync: addHistoryTechSkill,
    isPending: isAddingTechSkillHistory,
  } = useHistoryTechSkillsAdd(profileId);

  const convertDuplicatedPropertyMessageToArray = (message: string) => {
    const mapping = {
      COMPETENCE: "competence",
      SOFTWARE: "software",
      STREAM: "stream",
      COMPONENT: "component",
    };

    // eslint-disable-next-line no-useless-escape
    const keys = message.replace(/[\[\]\s]/g, "").split(",");
    return keys.map((key) => mapping[key as keyof typeof mapping]);
  };

  const handleSaveAddTech = async (
    data: FormDataTech,
    setError: UseFormSetError<FormDataTech>,
  ) => {
    try {
      setIsDuplicateError(false);
      const AddTechSkillRequest: AddTechSkillRequest = {
        competence: data.competence,
        stream: data.stream,
        software: data.software,
        component: data.component ?? "",
        description: data.description ?? "",
      };
      const response = await addTechSkill(AddTechSkillRequest);
      const AddHistoryTech: AddCollaboratorsHistoryTechSkillRequestList = [
        {
          techSkillId: response.id,
          level: data.level ?? orgSettings.techSkills.levels[0].value,
        },
      ];
      await addHistoryTechSkill(AddHistoryTech);
      onCancel();
      onConfirm();
    } catch (error) {
      const errorObj = error as AxiosError<SingleErrorResponse>;
      if (
        errorObj.response &&
        errorObj.response.status === StatusCodes.CUSTOM_ERROR
      ) {
        if (
          errorObj.response.data.code ===
          CustomerErrorCodesEnum.DUPLICATED_ENTITY
        ) {
          setIsDuplicateError(true);
        } else if (
          errorObj.response.data.code ===
          CustomerErrorCodesEnum.TECH_SKILL_DUPLICATED_PROPERTY
        ) {
          const errorPropertyList = convertDuplicatedPropertyMessageToArray(
            errorObj.response.data.message,
          );
          errorPropertyList.forEach((property) => {
            setError(property as keyof FormDataTech, {
              type: "custom",
              message: TECH_SKILL_DUPLICATE_PROP_ERROR_MESSAGE,
            });
          });
        }
      } else {
        console.error(`Error updating history tech skills: ${error}`);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
        onCancel();
      }
    }
  };

  const handleSaveAttachTech = async () => {
    if (selectedTechSkills.length === 0) {
      return;
    }
    const body: AddCollaboratorsHistoryTechSkillRequestList =
      selectedTechSkills.map((skill) => ({
        techSkillId: skill.id,
        level: skill.level ?? orgSettings.techSkills.levels[0].value,
      }));
    await addHistoryTechSkill(body);
    onCancel();
    onConfirm();
  };

  return (
    <Modal
      label={
        mode === TechSkillModalMode.ADD_TECH_SKILL
          ? `${ADD_NEW_TECH_SKILL}`
          : `${ATTACH_TECH_SKILL}`
      }
      isOpen={isOpen}
      {...(isDuplicateError && {
        message: (
          <StyledAlertMessage>
            <IconManager size={IconSizeEnum.SMALL} color={theme.colors.red_1}>
              <WarningIcon />
            </IconManager>
            <BodyTyp
              color={theme.colors.red_1}
              fontWeight={FontWeightEnum.SEMIBOLD}
            >
              {TECH_SKILL_DUPLICATE_ENTITY_ERROR_MESSAGE}
            </BodyTyp>
          </StyledAlertMessage>
        ),
      })}
      closeModal={
        mode === TechSkillModalMode.ADD_TECH_SKILL
          ? () => setMode(TechSkillModalMode.ATTACH)
          : onCancel
      }
      leftButton={
        <Button
          label={CANCEL_LABEL}
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          onClick={() => {
            if (mode === TechSkillModalMode.ADD_TECH_SKILL) {
              setMode(TechSkillModalMode.ATTACH);
            } else {
              onCancel();
            }
          }}
          disabled={isAddingTechSkill || isAddingTechSkillHistory}
        />
      }
      rightButton={
        <Button
          label={
            mode === TechSkillModalMode.ADD_TECH_SKILL
              ? `${SAVE_LABEL}`
              : `${CONFIRM_LABEL}`
          }
          icon={
            mode === TechSkillModalMode.ADD_TECH_SKILL ? (
              <SaveIcon />
            ) : (
              <ConfirmIcon />
            )
          }
          disabled={
            mode === TechSkillModalMode.ADD_TECH_SKILL
              ? !isDirty || isAddingTechSkill || isAddingTechSkillHistory
              : selectedTechSkills.length === 0 ||
                isAddingTechSkill ||
                isAddingTechSkillHistory
          }
          {...(mode === TechSkillModalMode.ADD_TECH_SKILL && {
            type: "submit",
            form: "addTechSkillForm",
          })}
          {...(mode === TechSkillModalMode.ATTACH && {
            onClick: handleSaveAttachTech,
          })}
        />
      }
    >
      <StyledHistoryTechSkillModal>
        <Loader loading={isAddingTechSkill || isAddingTechSkillHistory}>
          {mode === TechSkillModalMode.ADD_TECH_SKILL && (
            <TechSkillForm
              onSubmit={handleSaveAddTech}
              formID="addTechSkillForm"
              showLevel
              record={
                record
                  ? {
                      ...record,
                      level: record.level,
                    }
                  : undefined
              }
              setIsDirty={setIsDirty}
              isLevelRequired
            />
          )}
          {mode === TechSkillModalMode.ATTACH && (
            <AttachHistoryTechSkillTable
              onAdd={() => setMode(TechSkillModalMode.ADD_TECH_SKILL)}
              setIsDirty={setIsDirty}
              selectedTechSkills={selectedTechSkills}
              setSelectedTechSkills={setSelectedTechSkills}
              collaborator={collaborator}
            />
          )}
        </Loader>
      </StyledHistoryTechSkillModal>
    </Modal>
  );
};
